export type PasswordPolicy = {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
};

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
};

const SPECIAL_CHARS = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

export type PasswordStrengthResult = {
  valid: boolean;
  errors: string[];
  score: number; // 0-4
  label: 'Weak' | 'Fair' | 'Good' | 'Strong' | 'Too short';
  checks: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
};

export function evaluatePassword(
  password: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY
): PasswordStrengthResult {
  const value = password || '';
  const checks = {
    length: value.length >= policy.minLength,
    uppercase: !policy.requireUppercase || /[A-Z]/.test(value),
    lowercase: !policy.requireLowercase || /[a-z]/.test(value),
    number: !policy.requireNumbers || /\d/.test(value),
    special: !policy.requireSpecialChars || SPECIAL_CHARS.test(value),
  };

  const errors: string[] = [];
  if (!checks.length) errors.push(`At least ${policy.minLength} characters`);
  if (!checks.uppercase) errors.push('One uppercase letter');
  if (!checks.lowercase) errors.push('One lowercase letter');
  if (!checks.number) errors.push('One number');
  if (!checks.special) errors.push('One special character');

  let score = 0;
  if (checks.length) score += 1;
  if (value.length >= policy.minLength + 4) score += 1;
  if (checks.uppercase && checks.lowercase) score += 1;
  if (checks.number) score += 1;
  if (checks.special) score += 1;
  score = Math.min(score, 4);

  let label: PasswordStrengthResult['label'] = 'Too short';
  if (value.length === 0) label = 'Too short';
  else if (score <= 1) label = 'Weak';
  else if (score === 2) label = 'Fair';
  else if (score === 3) label = 'Good';
  else label = 'Strong';

  return {
    valid: errors.length === 0,
    errors,
    score,
    label,
    checks,
  };
}
